import React from 'react';
import { useJobStream } from '../../hooks/useJobStream';
import { useLanguage } from '../../hooks/useLanguage';
import jsonAnimation from "../../assets/thinking-animation.gif";

export const JobStreamDisplay = ({ jobId, onStatusUpdate }) => {
  const [isSpanish] = useLanguage();
  const { status, thinking, text, usage, isThinking, sectionKey } = useJobStream(jobId);
  const [fadeKey, setFadeKey] = React.useState(0);
  const thinkingContentRef = React.useRef(null);
  const textContentRef = React.useRef(null);

  // Handle fade animation when section changes (non-blocking)
  React.useEffect(() => {
    if (sectionKey > 0) {
      // Trigger fade animation by updating key, but don't block content updates
      setFadeKey(prev => prev + 1);
    }
  }, [sectionKey]);

  // Auto-scroll thinking content to bottom when it updates
  React.useEffect(() => {
    if (thinkingContentRef.current && thinking) {
      thinkingContentRef.current.scrollTop = thinkingContentRef.current.scrollHeight;
    }
  }, [thinking]);

  // Auto-scroll text content to bottom when it updates
  React.useEffect(() => {
    if (textContentRef.current && text) {
      textContentRef.current.scrollTop = textContentRef.current.scrollHeight;
    }
  }, [text]);

  // Track last status to prevent infinite loops - use a more stable key
  const lastStatusRef = React.useRef(null);
  const lastUpdateRef = React.useRef(null);
  const updateInProgressRef = React.useRef(false);
  
  // Update parent component when isThinking changes
  React.useEffect(() => {
    if (!onStatusUpdate) return;
    onStatusUpdate({ isThinking });
  }, [isThinking, onStatusUpdate]);

  // Update parent component when status changes
  React.useEffect(() => {
    if (!status || !onStatusUpdate) return;
    
    // Prevent concurrent updates
    if (updateInProgressRef.current) {
      return;
    }
    
    // Create a stable key from status properties
    const statusKey = JSON.stringify({
      id: status.id || jobId,
      status: status.status,
      ocrInputId: status.ocrInputId,
      hasResult: !!status.result
    });
    
    // Prevent infinite loops by checking if status actually changed
    if (lastStatusRef.current === statusKey) {
      return; // Status hasn't changed, skip update
    }
    lastStatusRef.current = statusKey;
    updateInProgressRef.current = true;
    
    // CRITICAL: Check if document_ready has been received - if so, NEVER send result data
    // This prevents ANY callback from updating extractedData after user has made changes
    const documentReadyReceived = sessionStorage.getItem('documentReadyReceived');
    
    // Check if this status update is from document_ready event (indicated by stage)
    const isFromDocumentReady = status.stage === 'document_ready';
    
    // Handle document_ready - processing is complete, modal can close
    // IMPORTANT: Only mark steps as completed when document_ready has been received
    // A status event with 'completed' can come before document_ready, so we wait for document_ready
    if (status.status === 'completed' || status.result) {
      // Mark as completed if:
      // 1. document_ready flag is set, OR
      // 2. This status update is directly from document_ready event (stage === 'document_ready')
      if (documentReadyReceived === 'true' || isFromDocumentReady) {
        // Only log once per unique status to avoid spam
        const blockedKey = `${statusKey}-blocked`;
        if (lastUpdateRef.current !== blockedKey && documentReadyReceived === 'true') {
          console.log('BLOCKED: JobStreamDisplay - not sending result data after document_ready to preserve user changes');
          lastUpdateRef.current = blockedKey;
        }
        const updateData = {
          areStepsCompleted: true,
          currentStep: 2,
          completedSteps: [0, 1, 2],
          processingStatus: 'completed',
          jobId: status.id || jobId,
          // CRITICAL: Do NOT include result if document_ready was already received - this would trigger extractedData updates
          // But if this IS the document_ready event, we can include result for initial data load
          ...(documentReadyReceived === 'true' ? {} : { result: status.result }),
          ocrInputId: status.ocrInputId
        };
        onStatusUpdate(updateData);
      } else {
        // document_ready not received yet - don't mark as completed
        // The stream is still processing, just update status but keep processing
        console.log('[JobStreamDisplay] Status is completed but document_ready not received yet - keeping processing state');
        const updateData = {
          areStepsCompleted: false, // Don't mark as completed yet
          currentStep: 1, // Keep at processing step
          completedSteps: [0], // Only step 0 is done
          processingStatus: 'processing', // Keep as processing
          jobId: status.id || jobId,
          result: status.result, // Can include result for data updates
          ocrInputId: status.ocrInputId
        };
        onStatusUpdate(updateData);
      }
    } else if (status.status === 'failed' || status.status === 'error') {
      onStatusUpdate({
        processingStatus: 'error',
        error: status.message || status.error || 'Document processing failed',
        jobId: status.id || jobId
      });
    } else {
      onStatusUpdate({
        processingStatus: status.status || 'processing',
        jobId: status.id || jobId,
        isThinking: isThinking
      });
    }
    
    // Reset the update flag after a short delay to allow state to settle
    setTimeout(() => {
      updateInProgressRef.current = false;
    }, 0);
  }, [status, onStatusUpdate, jobId]);

  // Function to convert markdown headers and bold text to styled elements
  const formatMarkdownText = (content) => {
    if (!content) return content;
    
    // Process bold markdown (**text**)
    const processBold = (text) => {
      const parts = [];
      let lastIndex = 0;
      const boldRegex = /\*\*(.+?)\*\*/g;
      let match;
      
      while ((match = boldRegex.exec(text)) !== null) {
        // Add text before the bold
        if (match.index > lastIndex) {
          parts.push(text.substring(lastIndex, match.index));
        }
        // Add bold text
        parts.push(<strong key={match.index} className="!font-semibold">{match[1]}</strong>);
        lastIndex = match.index + match[0].length;
      }
      // Add remaining text
      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }
      
      return parts.length > 0 ? parts : text;
    };
    
    // Convert markdown headers (###, ##, #) to styled elements
    return content.split('\n').map((line, index) => {
      // Match markdown headers: ###, ##, or #
      const headerMatch = line.match(/^(#{1,3})\s+(.+)$/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        const text = headerMatch[2];
        const className = level === 1 
          ? '!text-base !font-bold !mt-4 !mb-2 !text-gray-900' 
          : level === 2 
          ? '!text-sm !font-semibold !mt-3 !mb-2 !text-gray-800' 
          : '!text-xs !font-semibold !mt-2 !mb-1 !text-gray-700';
        
        return (
          <div key={index} className={className}>
            {processBold(text)}
          </div>
        );
      }
      // Process regular lines with potential bold text
      return (
        <div key={index} className="text-xs">
          {processBold(line || '\u00A0')}
        </div>
      );
    });
  };

  // Don't render anything if no jobId or no content
  if (!jobId) {
    return null;
  }

  return (
    <div className="mt-4 space-y-3 h-[280px] overflow-y-auto">
      {/* Thinking section - show with pulsing animation when actively thinking */}
      {isThinking && (
        <div className="thinking-section shine">
          <div className="flex items-center gap-2 mb-3 p-1">
            <img
              src={jsonAnimation}
              alt="Thinking"
              className="w-4 h-4"
            />
            <div className="thinking-section-header text-xs font-semibold !text-default !font-normal">
              <span className="inline-block animate-pulse">{isSpanish ? 'Pensando...' : 'Thinking...'}</span>
            </div>
          </div>
          {thinking && thinking.trim().length > 0 ? (
            <div 
              ref={thinkingContentRef}
              key={fadeKey}
              className="text-xs leading-relaxed fade-in-section max-h-[300px] overflow-y-auto scrollbar-hide"
            >
              {formatMarkdownText(thinking)}
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic">
              <span className="inline-block animate-pulse">{isSpanish ? 'Analizando documentos...' : 'Analyzing documents...'}</span>
            </div>
          )}
        </div>
      )}

      {/* Text/Output section - show only if there's text content */}
      {text && text.trim().length > 0 && (
        <div className="">
          <div className="flex items-center gap-2 mb-2">
            <img
              src={jsonAnimation}
              alt="Processing"
              className="w-4 h-4"
            />
            <div className="thinking-section-header text-xs font-semibold !text-default !font-normal">
              {isSpanish ? 'Procesando...' : 'Processing...'}
            </div>
          </div>
          <div className="relative">
            <div 
              ref={textContentRef}
              className="text-xs text-gray-800 whitespace-pre-wrap leading-relaxed prose prose-sm max-w-none font-mono text-xs max-h-[300px] overflow-y-auto scrollbar-hide"
            >
              {text}
            </div>
            <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-white to-transparent pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
          </div>
        </div>
      )}
    </div>
  );
};

